import { useEffect, useId, useMemo, useState } from 'react'
import { Icon } from '@iconify/react'
import { Link } from 'react-router-dom'
import { fetchCampaignQuestionAnswers } from '@/lib/campaign-questions'
import { formatCurrency, formatDateTime } from '@/lib/campaign-dashboard'
import { formatOptionResponseLabel } from '@/lib/campaign-question-stats'
import type {
  CampaignQuestion,
  CampaignQuestionAnswersResponse,
} from '@/types/campaign-question'
import styles from './QuestionAnswersTable.module.css'

interface QuestionAnswersTableProps {
  campaignId: number
  question: CampaignQuestion
  token: string
}

export function QuestionAnswersTable(props: QuestionAnswersTableProps) {
  const [isOpen, setIsOpen] = useState(false)
  const panelId = useId()

  return (
    <section className={styles.section} aria-label={`Respostas: ${props.question.title}`}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={isOpen}
        aria-controls={panelId}
        onClick={() => setIsOpen((open) => !open)}
      >
        <Icon icon="mdi:table-account" aria-hidden />
        <span>
          {isOpen
            ? 'Ocultar respostas dos participantes'
            : 'Ver respostas dos participantes'}
        </span>
        <Icon icon={isOpen ? 'mdi:chevron-up' : 'mdi:chevron-down'} aria-hidden />
      </button>
      <div id={panelId} hidden={!isOpen}>
        {isOpen && (
          <AnswersContent key={`${props.campaignId}-${props.question.id}`} {...props} />
        )}
      </div>
    </section>
  )
}

function AnswersContent({ campaignId, question, token }: QuestionAnswersTableProps) {
  const [filters, setFilters] = useState({ page: 1, optionId: '', search: '' })
  const [searchDraft, setSearchDraft] = useState('')
  const [retry, setRetry] = useState(0)
  const request = useMemo(
    () => ({ campaignId, questionId: question.id, token, filters, retry }),
    [campaignId, question.id, token, filters, retry]
  )
  const [result, setResult] = useState<{
    request: typeof request
    data?: CampaignQuestionAnswersResponse
    error?: string
  } | null>(null)
  const isLoading = result?.request !== request
  const data = isLoading ? null : result?.data
  const error = isLoading ? null : result?.error
  const searchId = useId()
  const optionId = useId()

  useEffect(() => {
    const controller = new AbortController()
    void fetchCampaignQuestionAnswers(
      request.campaignId,
      request.questionId,
      request.token,
      request.filters,
      controller.signal
    )
      .then((data) => {
        if (!controller.signal.aborted) setResult({ request, data })
      })
      .catch(() => {
        if (!controller.signal.aborted)
          setResult({ request, error: 'Não foi possível carregar as respostas.' })
      })

    return () => controller.abort()
  }, [request])

  const pagination = data?.pagination

  return (
    <div className={styles.content}>
      <p className={styles.intro}>
        Consulte quem respondeu e filtre pela alternativa escolhida. Cada linha
        corresponde a uma resposta em um pedido; a mesma pessoa pode aparecer em pedidos
        diferentes.
      </p>
      <form
        className={styles.filters}
        onSubmit={(event) => {
          event.preventDefault()
          setFilters((current) => ({ ...current, search: searchDraft.trim(), page: 1 }))
        }}
      >
        <div className={styles.field}>
          <label htmlFor={optionId}>Filtrar por resposta</label>
          <select
            id={optionId}
            value={filters.optionId}
            onChange={(event) =>
              setFilters((current) => ({
                ...current,
                optionId: event.target.value,
                page: 1,
              }))
            }
          >
            <option value="">Todas as respostas</option>
            {question.options.map((option) => (
              <option key={option.id} value={option.id}>
                {option.label} · {formatOptionResponseLabel(option.response_count)}
              </option>
            ))}
          </select>
        </div>
        <div className={`${styles.field} ${styles.searchField}`}>
          <label htmlFor={searchId}>Buscar participante</label>
          <input
            id={searchId}
            type="search"
            placeholder="Nome, e-mail ou telefone"
            maxLength={150}
            value={searchDraft}
            onChange={(event) => setSearchDraft(event.target.value)}
          />
        </div>
        <button type="submit" className={styles.button}>
          Buscar
        </button>
        {(filters.optionId || filters.search || searchDraft) && (
          <button
            type="button"
            className={styles.button}
            onClick={() => {
              setSearchDraft('')
              setFilters({ page: 1, optionId: '', search: '' })
            }}
          >
            Limpar filtros
          </button>
        )}
      </form>

      <div aria-live="polite" role="status" className={styles.status}>
        {isLoading
          ? 'Carregando respostas...'
          : pagination &&
            `${pagination.total} ${pagination.total === 1 ? 'resposta encontrada' : 'respostas encontradas'}`}
      </div>
      {error && (
        <div className={styles.error} role="alert">
          <span>{error}</span>
          <button
            type="button"
            className={styles.button}
            onClick={() => setRetry((value) => value + 1)}
          >
            Tentar novamente
          </button>
        </div>
      )}
      {data && data.responses.length === 0 && (
        <p className={styles.empty}>
          Nenhuma resposta encontrada para os filtros selecionados.
        </p>
      )}
      {data && data.responses.length > 0 && (
        <>
          <div
            className={styles.tableScroll}
            role="region"
            aria-label="Tabela de respostas"
            tabIndex={0}
          >
            <table className={styles.table}>
              <caption className={styles.caption}>{question.title}</caption>
              <thead>
                <tr>
                  <th scope="col">Participante</th>
                  <th scope="col">Contato</th>
                  <th scope="col">Resposta</th>
                  <th scope="col">Respondido em</th>
                  <th scope="col">Pedido</th>
                  <th scope="col">Valor do pedido</th>
                  <th scope="col">Pago em</th>
                </tr>
              </thead>
              <tbody>
                {data.responses.map((answer) => (
                  <tr key={answer.id}>
                    <td>
                      <strong>{answer.donor_name || 'Nome não informado'}</strong>
                      <span className={styles.secondary}>Cliente #{answer.donor_id}</span>
                    </td>
                    <td>
                      <span>{answer.email || 'E-mail não informado'}</span>
                      <span className={styles.secondary}>
                        {answer.phone || 'Telefone não informado'}
                      </span>
                    </td>
                    <td>
                      <span className={styles.answer}>{answer.option_label}</span>
                    </td>
                    <td>{formatDateTime(answer.answered_at)}</td>
                    <td>
                      {answer.payment_id ? (
                        <Link to={`/pedidos/lista/pedido/${answer.payment_id}`}>
                          #{answer.payment_id}
                        </Link>
                      ) : (
                        '—'
                      )}
                      {answer.quantity !== null && (
                        <span className={styles.secondary}>
                          {answer.quantity} {answer.quantity === 1 ? 'título' : 'títulos'}
                        </span>
                      )}
                    </td>
                    <td>
                      {answer.amount === null ? '—' : formatCurrency(answer.amount)}
                    </td>
                    <td>{formatDateTime(answer.paid_at)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {pagination && pagination.last_page > 1 && (
            <nav className={styles.pagination} aria-label="Paginação das respostas">
              <span>
                Página {pagination.current_page} de {pagination.last_page}
              </span>
              <div>
                <button
                  type="button"
                  className={styles.button}
                  disabled={pagination.current_page <= 1}
                  onClick={() =>
                    setFilters((current) => ({ ...current, page: current.page - 1 }))
                  }
                >
                  Anterior
                </button>
                <button
                  type="button"
                  className={styles.button}
                  disabled={pagination.current_page >= pagination.last_page}
                  onClick={() =>
                    setFilters((current) => ({ ...current, page: current.page + 1 }))
                  }
                >
                  Próxima
                </button>
              </div>
            </nav>
          )}
        </>
      )}
    </div>
  )
}
